import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Lottie from "lottie-react";
import dayjs from "dayjs";
import "dayjs/locale/th";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faChevronLeft, faShareNodes, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

import { ProviderContext } from "../../contexts/provider.context";
import {
    getCandidateByID,
    getReportById,
    checkReportMultipleChoice,
    getCompany,
    UnlockReport,
    getInterviewQuestionById,
    getInterviewQuestionByIdAndUserID,
    getJobListFromShanti,
    postForceRegenerateReport,
} from "../../services/api.service";
import tokenService from "../../services/token.service";
import { Candidate } from "../../model/candidate.model";
import { ReportModel } from "../../model/report.modelt";
import { QuestionsSummaryModel } from "../../model/questions-summary.model";
import { ResultMultipleChoice } from "../../model/summary-multiple-choice.model";
import LOADING_JSON from "../../assets/json/loading.json";
import COIN_IMG from "../../assets/images/currency-coin.png";
import AI_WHITE from "../../assets/images/ai-icon.png";
import RESUME_PURPLE from "../../assets/images/resume-purple.png";

import CandidateSummary from "../job-questions-list/job-question-detail/candidate-summary/candidate-summary.component";
import CandidateReport from "../job-questions-list/job-question-detail/candidate-report/candidate-report.component";
import ShareCandidateModal from "../../modal/share-candidate.modal";
import ConfirmGeneralModal from "../../modal/confirm-general.modal";
import CreditEmpty from "../../shared/credit-empty/credit-empty.shared";

import "./candidate-detail.page.scss";

// ── helpers ──────────────────────────────────────────────────
const initials = (c?: Candidate) => {
    if (!c) return "?";
    return `${(c.firstname?.[0] || "").toUpperCase()}${(c.lastname?.[0] || "").toUpperCase()}` || "?";
};

const formatDateThai = (iso?: string) => {
    if (!iso) return "-";
    const d = dayjs(iso).locale("th");
    return `${d.format("D MMM")} ${d.year() + 543}`;
};

const statusOf = (reportModel?: ReportModel, candidateProfile?: Candidate) => {
    if (!candidateProfile?.report_id)                        return { label: "ยังไม่ทำ",  cls: "badge-pending" };
    if (reportModel?.credit_status === "INSUFFICIENT_FUNDS") return { label: "ล็อค",       cls: "badge-locked"  };
    if (reportModel)                                          return { label: "เสร็จสิ้น", cls: "badge-complete" };
    return                                                           { label: "กำลังโหลด", cls: "badge-pending" };
};

// ─────────────────────────────────────────────────────────────
const CandidateDetailPage = () => {
    const { profileModel }: any = useContext(ProviderContext);
    const { company_id, interview_id, candidate_id } = useParams<{
        company_id: string;
        interview_id: string;
        candidate_id: string;
    }>();
    const navigate = useNavigate();

    const [candidateProfile, setCandidateProfile] = useState<Candidate | undefined>();
    const [reportModel, setReportModel]             = useState<ReportModel | undefined>();
    const [loadingReport, setLoadingReport]         = useState(true);
    const [isConfirmUnlock, setIsConfirmUnlock]     = useState(false);
    const [isCandidateShare, setIsCandidateShare]   = useState(false);
    const [resultMultipleChoice, setResultMultipleChoice] = useState<ResultMultipleChoice[]>([]);
    const [creditModel, setCreditModel]             = useState<any>();
    const [sectionList, setSectionList]             = useState({ summary: true, ai_summary: true });
    const [interviewModel, setInterviewModel]       = useState<QuestionsSummaryModel | undefined>();
    const [jobList, setJobList]                     = useState<any[]>([]);
    const [mappedJobIdShanti, setMappedJobIdShanti] = useState<string | null>(null);
    const [isProfiForceRegenerating, setIsProfiForceRegenerating] = useState(false);

    useEffect(() => {
        setMappedJobIdShanti((interviewModel as any)?.["job-id-shanti"] ?? null);
    }, [interviewModel]);

    useEffect(() => {
        if (!company_id) return;
        getCompany(company_id).then((res) => {
            if (res.data.is_success) setCreditModel(res.data.message);
        });
    }, [company_id]);

    useEffect(() => {
        if (!interview_id || !candidate_id) return;
        setLoadingReport(true);
        setReportModel(undefined);
        fetchCandidate(interview_id, candidate_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interview_id, candidate_id]);

    useEffect(() => {
        if (!interview_id) return;
        if (!profileModel?.roleName) return;

        const fetchInterview =
            profileModel.roleName === "ROLE_SUPER_ADMIN"
                ? getInterviewQuestionById(interview_id)
                : getInterviewQuestionByIdAndUserID(interview_id, profileModel.uuid);

        fetchInterview
            .then((res) => {
                if (res.data.is_success) {
                    setInterviewModel(res.data.data as QuestionsSummaryModel);
                } else {
                    navigate("/" + tokenService.getHR9CompanyUUID() + "/job-questions-list");
                }
            })
            .catch(() => navigate("/" + tokenService.getHR9CompanyUUID() + "/job-questions-list"));

        if (company_id) {
            getJobListFromShanti(company_id)
                .then((res) => { if (res.data.is_success) setJobList(res.data.data || []); })
                .catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interview_id, profileModel?.roleName, profileModel?.uuid, company_id]);

    const fetchReport = (report_id: string) => {
        getReportById(report_id)
            .then((res) => {
                if (res.data.is_success) {
                    setReportModel(res.data.data as ReportModel);
                    setLoadingReport(false);
                }
            })
            .catch(() => setLoadingReport(false));
    };

    const fetchMultipleChoice = (report_id: string) => {
        checkReportMultipleChoice(report_id, interview_id, candidate_id)
            .then((res) => {
                if (res.data.is_success) setResultMultipleChoice(res.data.data as ResultMultipleChoice[]);
            })
            .catch(console.error);
    };

    const fetchCandidate = (interviewId: string, candidateId: string) => {
        getCandidateByID(interviewId, candidateId)
            .then((res) => {
                if (res.data.is_success) {
                    const profile = res.data.data as Candidate;
                    setCandidateProfile(profile);
                    if (profile.report_id) {
                        fetchReport(profile.report_id);
                        fetchMultipleChoice(profile.report_id);
                    } else {
                        setLoadingReport(false);
                    }
                }
            })
            .catch(() => {
                setLoadingReport(false);
                navigate("/" + tokenService.getHR9CompanyUUID() + "/job/" + interviewId);
            });
    };

    const updateSection = (field: string) => {
        setSectionList((prev) => ({
            ...prev,
            ...(field === "summary"    ? { summary:    !prev.summary    } : {}),
            ...(field === "ai_summary" ? { ai_summary: !prev.ai_summary } : {}),
        }));
    };

    const unlockCandidate = () => {
        if (!candidate_id || !reportModel || !candidateProfile) return;
        UnlockReport(
            reportModel.report_id,
            company_id,
            candidate_id,
            `${candidateProfile.firstname} ${candidateProfile.lastname}`,
        ).then((res) => {
            if (res.data.is_success) {
                navigate("/" + tokenService.getHR9CompanyUUID() + "/job/" + reportModel.job_question_id + "/" + candidate_id);
                setLoadingReport(true);
                fetchReport(reportModel.report_id);
                setIsConfirmUnlock(false);
            }
        }).catch((err) => {
            alert(err);
            setIsConfirmUnlock(false);
        });
    };

    const candidateFullName = candidateProfile
        ? `${candidateProfile.firstname} ${candidateProfile.lastname}`.trim()
        : "";

    const interviewStatusLabel =
        candidateProfile?.interview_status === "COMPLETED"
            ? "สัมภาษณ์เสร็จสิ้น"
            : candidateProfile?.interview_status || "ยังไม่เริ่ม";

    const { label: statusLabel, cls: statusCls } = statusOf(reportModel, candidateProfile);
    const mappedJob = mappedJobIdShanti ? jobList.find((j) => j.uuid === mappedJobIdShanti) : undefined;
    const isGeneralQuestionSet =
        String(interviewModel?.job_position?.question_type || "").toLowerCase() === "general";

    // ── View full report shortcut ─────────────────────────
    const handleViewFullReport = () => {
        if (reportModel?.report_id) {
            window.open(`${window.location.origin}/public-report/${reportModel.report_id}`, "_blank");
        }
    };

    const handleProfiForceRegenerate = async () => {
        if (!reportModel?.report_id) return;
        setIsProfiForceRegenerating(true);
        try {
            const response = await postForceRegenerateReport(reportModel.report_id, {
                force: true,
                send_email: true,
            });
            if (!response?.data?.is_success) {
                throw new Error(
                    response?.data?.error || response?.data?.message || "Force regenerate failed",
                );
            }
            alert(response?.data?.message || "Force regenerate สำเร็จ");
            fetchReport(reportModel.report_id);
        } catch (e) {
            console.error("Force regenerate:", e);
            alert("Force regenerate ไม่สำเร็จ — ลองใหม่หรือดู Console / Network");
        } finally {
            setIsProfiForceRegenerating(false);
        }
    };

    return (
        <div className="cd-page">
            {/* ══ Sticky top bar ═══════════════════════════ */}
            <div className="cd-topbar">
                <div className="cd-breadcrumb">
                    <button
                        className="cd-breadcrumb-btn"
                        onClick={() => navigate(`/${company_id}/job/${interview_id}`)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span>{interviewModel?.job_position?.name || "รายชื่อผู้สมัคร"}</span>
                    </button>
                    {candidateFullName && (
                        <>
                            <span className="cd-breadcrumb-sep">/</span>
                            <span className="cd-breadcrumb-current">{candidateFullName}</span>
                        </>
                    )}
                </div>
                <div className="cd-topbar-actions">
                    {!loadingReport && (
                        <span className={`cd-status-badge ${statusCls}`}>
                            <span className="cd-badge-dot" />
                            {statusLabel}
                        </span>
                    )}
                    {reportModel?.interview_status === "COMPLETED" && (
                        <button className="cd-icon-btn" onClick={handleViewFullReport}>
                            <FontAwesomeIcon icon={faUpRightFromSquare} style={{ fontSize: 12 }} />
                            Full Report
                        </button>
                    )}
                    {candidateProfile && interviewModel && (
                        <button className="cd-icon-btn" onClick={() => setIsCandidateShare(true)}>
                            <FontAwesomeIcon icon={faShareNodes} style={{ fontSize: 12 }} />
                            แชร์
                        </button>
                    )}
                </div>
            </div>

            {/* ══ Body ═════════════════════════════════════ */}
            {loadingReport ? (
                <div className="cd-loading">
                    <Lottie animationData={LOADING_JSON} className="cd-loading-json" />
                    <span>กำลังโหลดข้อมูลผู้สมัคร</span>
                </div>
            ) : (
                <div className="cd-body">
                    {/* ── Profile hero card ── */}
                    {candidateProfile && (
                        <div className="cd-profile-card">
                            {/* gradient banner */}
                            <div className="cd-profile-banner" />

                            {/* avatar overlapping banner */}
                            <div className="cd-avatar-wrap">
                                <div className="cd-avatar">
                                    <span className="cd-avatar-initials">{initials(candidateProfile)}</span>
                                </div>
                            </div>

                            {/* name + meta */}
                            <div className="cd-profile-info">
                                <span className="cd-profile-name">{candidateFullName}</span>
                                <span className="cd-profile-subtitle">
                                    {interviewModel?.job_position?.name || interviewModel?.job_position?.job_title || "-"}
                                </span>
                                <div className="cd-profile-meta">
                                    {candidateProfile.email && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Email</span>
                                            {candidateProfile.email}
                                        </span>
                                    )}
                                    {candidateProfile.phone && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Phone</span>
                                            {candidateProfile.phone}
                                        </span>
                                    )}
                                    {candidateProfile.jobTitle && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Title</span>
                                            {candidateProfile.jobTitle}
                                        </span>
                                    )}
                                    {candidateProfile.jobExp != null && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Exp</span>
                                            {candidateProfile.jobExp} yrs
                                        </span>
                                    )}
                                    {candidateProfile.created_at && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Applied</span>
                                            {formatDateThai(candidateProfile.created_at)}
                                        </span>
                                    )}
                                    {candidateProfile.interview_start_date && (
                                        <span className="cd-meta-chip">
                                            <span className="cd-chip-label">Interviewed</span>
                                            {formatDateThai(candidateProfile.interview_start_date)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* action buttons */}
                            <div className="cd-profile-actions">
                                {reportModel?.interview_status === "COMPLETED" && (
                                    <button className="cd-action-btn cd-action-primary" onClick={handleViewFullReport}>
                                        <img src={AI_WHITE} className="icon" alt="" />
                                        View Full Report
                                    </button>
                                )}
                                {reportModel?.interview_status === "COMPLETED" &&
                                    reportModel?.report_id && (
                                        <button
                                            type="button"
                                            className="cd-action-btn cd-action-outline cd-action-regen"
                                            title="รันวิเคราะห์รายงานใหม่"
                                            disabled={isProfiForceRegenerating}
                                            onClick={handleProfiForceRegenerate}
                                        >
                                            <FontAwesomeIcon icon={faArrowsRotate} className="cd-action-fa" />
                                            {isProfiForceRegenerating ? "กำลังรัน..." : "Force regenerate"}
                                        </button>
                                    )}
                                {candidateProfile.resume && (
                                    <button className="cd-action-btn cd-action-outline">
                                        <img src={RESUME_PURPLE} className="icon" alt="" />
                                        Resume
                                    </button>
                                )}
                            </div>

                            {/* stats strip */}
                            <div className="cd-profile-stats">
                                <div className="cd-stat-item">
                                    <span className="cd-stat-label">Interview</span>
                                    <span className="cd-stat-value">{interviewStatusLabel}</span>
                                </div>
                                <div className="cd-stat-item">
                                    <span className="cd-stat-label">Report ID</span>
                                    <span className="cd-stat-value">{candidateProfile.report_id || "—"}</span>
                                </div>
                                <div className="cd-stat-item">
                                    <span className="cd-stat-label">App</span>
                                    <span className="cd-stat-value">{candidateProfile.app_id || reportModel?.app_id || "—"}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Resume Summary card ── */}
                    {candidateProfile && (
                        <div className="cd-section-card">
                            <div className="cd-section-header">
                                <span className="cd-section-title">Candidate Summary</span>
                            </div>
                            <div className="cd-section-body">
                                <CandidateSummary
                                    candidateProfile={candidateProfile}
                                    sectionList={sectionList}
                                    updateSection={updateSection}
                                    interviewModel={interviewModel}
                                    reportModel={reportModel}
                                    hideActionButtons
                                />
                            </div>
                        </div>
                    )}

                    {/* ── Report card ── */}
                    <div className="cd-section-card">
                        <div className="cd-section-header">
                            <span className="cd-section-title">Interview Report</span>
                            {!loadingReport && (
                                <span className={`cd-status-badge ${statusCls}`}>
                                    <span className="cd-badge-dot" />
                                    {statusLabel}
                                </span>
                            )}
                        </div>
                        {reportModel?.credit_status === "INSUFFICIENT_FUNDS" ? (
                            <div className="cd-section-body-center">
                                {creditModel && (
                                    <CreditEmpty
                                        submit={() => setIsConfirmUnlock(true)}
                                        credit_remaining={creditModel.credits}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="cd-section-body">
                                <CandidateReport
                                    resultMultipleChoice={resultMultipleChoice}
                                    reportModel={reportModel}
                                    sectionList={sectionList}
                                    sendEmailAction={() => {}}
                                    updateSection={updateSection}
                                    job={mappedJob}
                                    isLoading={loadingReport}
                                    candidateAppId={candidateProfile?.app_id}
                                    jobCustomMeasurementCriteria={interviewModel?.custom_measurement_criteria ?? ""}
                                    generalQuestionSetMinimal={isGeneralQuestionSet}
                                    generalObjectiveText={String(
                                        (
                                            interviewModel?.job_position?.job_advance as
                                                | { job_description?: string }
                                                | undefined
                                        )?.job_description || "",
                                    ).trim()}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══ Modals ════════════════════════════════════ */}
            {isCandidateShare && candidateProfile && interviewModel && (
                <ShareCandidateModal
                    interview={interviewModel}
                    period="-"
                    job_position={interviewModel.job_position}
                    isOpen={isCandidateShare}
                    onClose={() => setIsCandidateShare(false)}
                    candidate={candidateProfile}
                />
            )}
            {isConfirmUnlock && (
                <ConfirmGeneralModal
                    buttonCancel="ยกเลิก"
                    buttonConfirm="ยืนยัน"
                    icon={COIN_IMG}
                    message="ยืนยันที่จะเปิดดูผู้สมัคร?"
                    message2=""
                    isOpen={isConfirmUnlock}
                    onClose={() => setIsConfirmUnlock(false)}
                    onConfrim={unlockCandidate}
                />
            )}
        </div>
    );
};

export default CandidateDetailPage;
